import { configureStore, PreloadedState } from '@reduxjs/toolkit';
import { combineReducers } from 'redux';
import achievementSlice from 'modules/outbound/fulfillment/redux/ListAchievementsReducer/index';
import filtersSlice from 'modules/outbound/fulfillment/redux/FiltersStateReducer/index';
import deliveriesGeolocationSlice from 'modules/outbound/fulfillment/redux/DeliveriesGeolocationReducer';
import deliveriesGeolocationCheckpointsSlice from 'modules/outbound/fulfillment/redux/DeliveriesGeolocationCheckpointsReducer';
import finishedGoodsByCheckpointReducer from 'modules/outbound/fulfillment/redux/FinishedGoodsByCheckpointReducer';
import geolocatedTransportsReducer from 'modules/outbound/fulfillment/redux/GeolocatedTransportsReducer';
import noJitDataReducer from 'modules/inbound/nojit/ui/redux/NoJitDataReducer';
import suppliesDataReducer from 'modules/inbound/supplies/ui/redux/SuppliesDataReducer';
import sharedDataReducer from 'modules/outbound/shared/ui/redux/SharedDataReducer';
import { transportCapacitiesReducer } from 'modules/outbound/capacities/ui/redux/TransportCapacitiesReducer';
import suppliesFiltersReducer from 'modules/inbound/supplies/ui/redux/SuppliesFiltersStateReducer';
import noJitFiltersReducer from 'modules/inbound/nojit/ui/redux/NoJitFiltersStateReducer';
import refillsDataReducer from 'modules/inbound/refills/ui/redux/RefillsDataReducer';
import endOfProductionDateDataState from 'modules/inbound/endOfProductionDate/ui/redux/EndOfProductionDateDataReducer';
import endOfProductionFiltersData from 'modules/inbound/endOfProductionDate/ui/redux/EndOfProductionFiltersReducer';
import usersDataState from 'modules/inbound/users/ui/redux/UsersDataReducer';
import partCoverageState from 'modules/inbound/jit/ui/redux/PartCoverageReducer';
import partCoverageFiltersState from 'modules/inbound/jit/ui/redux/PartCoverageFiltersReducer';
import jitFamilyCoverageState from 'modules/inbound/jit/ui/redux/JitFamilyCoverageDataReducer';
import familiesCoverageState from 'modules/inbound/jit/ui/redux/FamiliesCoverageReducer';
import jitFamiliesFiltersState from 'modules/inbound/jit/ui/redux/FamiliesCoverageFiltersReducer';
import directDeliveryState from 'modules/inbound/directDelivery/ui/redux/DirectDeliveryReducer';
import directDeliveryFiltersState from 'modules/inbound/directDelivery/ui/redux/DirectDeliveryFiltersReducer';
import upcomingTransitsState from 'modules/inbound/directDelivery/ui/redux/UpcomingTransitsReducer';
import transitsHistoryState from 'modules/inbound/directDelivery/ui/redux/TransitsHistoryReducer';
import transferReducer from 'modules/inbound/transfer/ui/redux/TransferReducer';
import transfersFiltersState from 'modules/inbound/transfer/ui/redux/TransfersFiltersReducer';
import containerNeedsState from 'modules/inbound/transfer/ui/redux/ContainerNeedsReducer';
import dischargeDocksReducer from 'modules/inbound/entries/ui/redux/DischargeDocksReducer';
import factoryBcnReducer from 'modules/inbound/factoryBcn/ui/redux/FactoryBcnReducer';
import factoryBcnFilters from 'modules/inbound/factoryBcn/ui/redux/FactoryBcnFiltersReducer';
import userInboundState from 'modules/inbound/shared/ui/redux/UserInboundReducer';
import entriesDataState from 'modules/inbound/entries/ui/redux/EntriesDataReducer';
import entriesInPlantState from 'modules/inbound/entries/ui/redux/EntriesInPlantReducer';
import entriesFiltersState from 'modules/inbound/entries/ui/redux/EntriesFiltersReducer';
import entriesInPlantFiltersState from 'modules/inbound/entries/ui/redux/EntriesInPlantFiltersReducer';
import sharedReducer from 'modules/shared/infrastructure/redux/SharedReducer';
import inboundMessagesState from 'modules/inbound/shared/ui/redux/InboundMessagesReducer';
import { userReducer } from 'modules/shared/infrastructure/redux/UserReducer';
import expirationsDataState from 'modules/inbound/expiration/ui/redux/ExpirationDataReducer';
import expirationFiltersData from 'modules/inbound/expiration/ui/redux/ExpirationFiltersReducer';
import { serviceLevelReducer } from 'modules/outbound/serviceLevel/ui/redux/ServiceLevelReducer';
import providerCodesReducer from 'modules/inbound/shared/ui/redux/ProviderCodesReducer';
import contactListState from 'modules/inbound/shared/ui/redux/ContactListReducer';
import saturationUnloadingPointsReducer from 'modules/inbound/entries/ui/redux/SaturationUnloadingPointsReducer';
import saturationUnloadingPointsFiltersReducer from 'modules/inbound/entries/ui/redux/SaturationUnloadingPointsFilters';
import contactFormState from 'modules/inbound/shared/ui/redux/ContactFormReducer';
import mastersState from 'modules/inbound/maestros/masters/ui/redux/MastersReducer';
import stucklisteSuppliersState from 'modules/inbound/stucklisteSuppliers/ui/redux/StucklisteSuppliersReducer';
import allProvidersState from 'modules/inbound/shared/ui/redux/AllProvidersReducer';
import partCoverageStateProgramCoverage from 'modules/inbound/programCoverage/ui/redux/PartCoverageReducer';
import partCoverageFiltersStateProgramCoverage from 'modules/inbound/programCoverage/ui/redux/PartCoverageFiltersReducer';

import entriesST12DataState from 'modules/inbound/entriesST12/ui/redux/EntriesDataReducer';
import entriesST12FiltersState from 'modules/inbound/entriesST12/ui/redux/EntriesFiltersReducer';
import entriesST12InPlantState from 'modules/inbound/entriesST12/ui/redux/EntriesInPlantReducer';
import entriesST12InPlantFiltersState from 'modules/inbound/entriesST12/ui/redux/EntriesInPlantFiltersReducer';
import saturationUnloadingPointsForST12 from 'modules/inbound/entriesST12/ui/redux/SaturationUnloadingPointsReducer';
import saturationUnloadingPointsForST12Filters from 'modules/inbound/entriesST12/ui/redux/SaturationUnloadingPointsFilters';

import thresholdDateSlice from 'modules/inbound/entriesST12/ui/redux/ThresholdReducer';
import systemAlertDateSlice from 'modules/inbound/entriesST12/ui/redux/SystemAlertReducer';

import chartDateSlice from 'modules/inbound/entriesST12/ui/redux/ChartDataReducer';
import trucksInfoSlice from 'modules/inbound/entriesST12/ui/redux/TrucksInfoReducer';
import unloadingPointSlice from 'modules/inbound/entriesST12/ui/redux/UnloadingPointReducer';

export const reducer = combineReducers({
  achievementState: achievementSlice.reducer,
  filtersState: filtersSlice.reducer,
  suppliesFiltersState: suppliesFiltersReducer.reducer,
  noJitFiltersState: noJitFiltersReducer.reducer,
  deliveriesGeolocationState: deliveriesGeolocationSlice.reducer,
  deliveriesGeolocationCheckpointsState:
    deliveriesGeolocationCheckpointsSlice.reducer,
  finishedGoodsByCheckpointState: finishedGoodsByCheckpointReducer,
  geolocatedTransportsState: geolocatedTransportsReducer,
  noJitDataState: noJitDataReducer,
  suppliesDataState: suppliesDataReducer,
  partCoverageState: partCoverageState,
  partCoverageFiltersState: partCoverageFiltersState,
  jitFamilyCoverageState: jitFamilyCoverageState,
  familiesCoverageState: familiesCoverageState,
  jitFamiliesFiltersState: jitFamiliesFiltersState,
  sharedDataState: sharedDataReducer,
  refillsDataState: refillsDataReducer,
  endOfProductionDateDataState: endOfProductionDateDataState,
  endOfProductionFiltersData: endOfProductionFiltersData,
  usersDataState: usersDataState,
  directDeliveryState: directDeliveryState,
  directDeliveryFiltersState: directDeliveryFiltersState,
  upcomingTransitsState: upcomingTransitsState,
  transitsHistoryState: transitsHistoryState,
  transferState: transferReducer,
  transfersFiltersState: transfersFiltersState,
  containerNeedsState: containerNeedsState,
  dischargeDocksState: dischargeDocksReducer,
  entriesDataState: entriesDataState,
  entriesInPlantState: entriesInPlantState,
  entriesFiltersState: entriesFiltersState,
  entriesInPlantFiltersState: entriesInPlantFiltersState,
  factoryBcnState: factoryBcnReducer,
  factoryBcnFilters: factoryBcnFilters,
  userInboundState: userInboundState,
  sharedState: sharedReducer,
  inboundMessagesState: inboundMessagesState,
  expirationFiltersData: expirationFiltersData,
  userState: userReducer,
  expirationsDataState: expirationsDataState,
  transportCapacitiesState: transportCapacitiesReducer,
  serviceLevelState: serviceLevelReducer,
  providerCodesState: providerCodesReducer,
  contactListState: contactListState,
  saturationUnloadingPointsState: saturationUnloadingPointsReducer,
  saturationUnloadingPointsFiltersState:
    saturationUnloadingPointsFiltersReducer,
  contactFormState: contactFormState,
  mastersState: mastersState,
  stucklisteSuppliersState: stucklisteSuppliersState,
  allProvidersState: allProvidersState,
  partCoverageStateProgramCoverage: partCoverageStateProgramCoverage,
  partCoverageFiltersStateProgramCoverage:
    partCoverageFiltersStateProgramCoverage,
  entriesST12DataState: entriesST12DataState,
  entriesST12FiltersState: entriesST12FiltersState,
  entriesST12InPlantState,
  entriesST12InPlantFiltersState,
  saturationUnloadingPointsForST12State: saturationUnloadingPointsForST12,
  saturationUnloadingPointsForST12FiltersState:
    saturationUnloadingPointsForST12Filters,
  chartDateSliceState: chartDateSlice,
  trucksInfoSliceState: trucksInfoSlice,
  thresholdDateSliceState: thresholdDateSlice,
  systemAlertDateSliceState: systemAlertDateSlice,
  unloadingPointSliceState: unloadingPointSlice
});

const store = configureStore({
  reducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false
    }),
  devTools: process.env.NODE_ENV !== 'production'
});

export const getNewStore = (preloadedState?: PreloadedState<AppState>) =>
  configureStore({
    reducer,
    preloadedState,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: false
      }),
    devTools: process.env.NODE_ENV !== 'production'
  });

export type AppState = ReturnType<typeof reducer>;
export type AppDispatch = typeof store.dispatch;
export default store;
